import { css } from "@emotion/react";
import styled from "@emotion/styled";
import type { ButtonProps } from "@mui/material";
import { ButtonGroup, Grow, IconButton, MenuItem, MenuList, Paper, Popper } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import type { ComponentProps, MouseEvent, ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { Loading, LoadingContainer } from "../common";
import { Icon } from "../icons";
import { zIndexes } from "../utilities";
import { CanvasToolbarButton } from "./CanvasToolbar";

type Option = Pick<ButtonProps, "onClick" | "disabled"> & {
  label: ReactNode;
  icon?: ReactNode;
};

type Props = Omit<ButtonProps, "type"> & {
  busy?: boolean;
  options: Option[];
  tooltip?: ReactNode;
  type?: "button" | "reset" | "submit";
};

const ContentContainer = styled.div<{ $showIcon: boolean }>`
  display: flex;
  gap: 8px;

  place-items: center;

  ${LoadingContainer} {
    width: initial;
  }
`;

const DropdownButton = styled(IconButton)`
  margin-left: -4px;
  padding: 0 2px;
`;

export const CanvasToolbarSplitButton = ({ busy, options, variant, disabled, tooltip, ...rest }: Props) => {
  const dataTest = (rest as Record<string, unknown>)["data-test"];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleClick = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);

  const handleClose: ComponentProps<typeof ClickAwayListener>["onClickAway"] = (e) => {
    if (anchorEl && anchorEl.contains(e.currentTarget as HTMLElement)) return;

    setAnchorEl(null);
  };

  const handleMenuItemClick = useCallback(
    (index: number) => (e: React.MouseEvent) => {
      setSelectedIndex(index);
      setAnchorEl(null);
      options[index].onClick?.(e as React.MouseEvent<HTMLButtonElement>);
    },
    [options],
  );

  const showDropdown = options.length > 1 && !disabled;
  const open = Boolean(anchorEl);

  const ActionButton = useMemo(
    () => (
      <CanvasToolbarButton
        onClick={options[selectedIndex].onClick}
        disabled={disabled}
        data-test="split-button-action-button"
        tooltip={tooltip}
        css={
          showDropdown
            ? css`
                width: 24px;
              `
            : undefined
        }
        {...rest}
      >
        <ContentContainer $showIcon={!!options[selectedIndex].icon}>
          {busy ? <Loading size={16} /> : options[selectedIndex].icon}
        </ContentContainer>
      </CanvasToolbarButton>
    ),
    [options, selectedIndex, disabled, tooltip, showDropdown, rest, busy],
  );

  return !showDropdown ? (
    ActionButton
  ) : (
    <>
      <ButtonGroup disabled={disabled} variant={variant} aria-label="split button" data-test={dataTest}>
        {ActionButton}
        <DropdownButton
          size="small"
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="menu"
          onClick={handleClick}
          data-test="canvas-split-button-open"
        >
          <Icon icon="angle-down" size={10} />
        </DropdownButton>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: zIndexes.composableNodeControlsBar }}
        open={open}
        anchorEl={anchorEl}
        role={undefined}
        transition
        disablePortal
        placement="bottom-end"
        data-test="split-button-menu-pop-up"
        {...{ nonce: undefined, onResize: undefined, onResizeCapture: undefined }} // Note: someone on the MUI team stuffed up types and made these required.
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === "bottom-end" ? "center top" : "center bottom" }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu-button" autoFocusItem data-test="list">
                  {options.map(({ label, icon, disabled: menuItemDisabled }, index) => (
                    <MenuItem
                      key={index}
                      selected={index === selectedIndex}
                      onClick={handleMenuItemClick(index)}
                      disabled={menuItemDisabled}
                      data-test="item"
                    >
                      <ContentContainer $showIcon={!!icon} data-test="content-container">
                        {icon}
                        {label}
                      </ContentContainer>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
